import { useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, MoreHorizontal, Phone, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES_DATA } from "@/data/services";

const LINKS = [
  { label: "Home", href: "/" },
  { label: "Pricing", href: "/pricing" },
  { label: "Inventory", href: "/inventory-calculator" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (href) => {
    setOpen(false);
    setServicesOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      lenis?.scrollTo(0, { immediate: true });
      return;
    }
    if (pathname !== "/") {
      navigate("/" + href);
      return;
    }
    lenis?.scrollTo(href, { offset: -70 });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-[0_1px_12px_rgba(15,23,42,0.06)] backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        <div className="flex items-center gap-3">
          <Logo onClick={() => go("#top")} testid="header-logo" iconSize="h-7 sm:h-8" textSize="text-sm min-[400px]:text-base sm:text-lg" />
          <a
            href="tel:0391136005"
            className="flex flex-col whitespace-nowrap leading-tight sm:hidden"
            data-testid="mobile-call-now"
            aria-label="Call MoversNearby now"
          >
            <span className="flex items-center gap-1 rounded-full bg-blaze px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider text-white">
              <Phone className="h-3 w-3" /> Call Now
            </span>
          </a>
        </div>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          <Link
            to="/"
            onClick={() => { setServicesOpen(false); lenis?.scrollTo(0, { immediate: true }); }}
            data-testid="nav-home"
            className={`group relative text-sm font-medium transition-colors duration-300 hover:text-slate-900 ${pathname === "/" ? "text-blaze" : "text-slate-700"}`}
          >
            Home
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-blaze transition-all duration-300 group-hover:w-full" />
          </Link>
          <div className="relative">
            <button
              onClick={() => setServicesOpen(!servicesOpen)}
              data-testid="nav-services"
              className={`group relative flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 hover:text-slate-900 ${pathname.startsWith("/services") ? "text-blaze" : "text-slate-700"}`}
            >
              Services
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-blaze transition-all duration-300 group-hover:w-full" />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-9 z-50 w-64 rounded-xl border border-slate-200 bg-white py-2 shadow-xl" data-testid="services-menu">
                {SERVICES_DATA.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setServicesOpen(false)}
                    data-testid={`nav-service-${s.slug}`}
                    className="block w-full px-5 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-blaze"
                  >
                    {s.nav}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {LINKS.slice(1).map((l) => (
            <Link
              key={l.label}
              to={l.href}
              onClick={() => setServicesOpen(false)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`group relative text-sm font-medium transition-colors duration-300 hover:text-slate-900 ${
                pathname === l.href ? "text-blaze" : "text-slate-700"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-blaze transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book-online"
            data-testid="header-book-online-btn"
            className="hidden border border-blaze px-6 py-3 text-sm font-bold uppercase tracking-wider text-blaze transition-all duration-300 hover:bg-blaze hover:text-white md:block"
          >
            Book Now Online
          </Link>
          <a
            href="tel:0391136005"
            data-testid="header-quote-btn"
            className="hidden items-center gap-2 bg-blaze px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.04] hover:bg-blue-700 sm:flex"
          >
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="p-2 text-slate-700 transition-colors hover:text-slate-900"
              data-testid="more-menu-toggle"
              aria-label="More options"
            >
              <MoreHorizontal className="h-6 w-6" />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                <div className="absolute right-0 top-12 z-50 w-56 rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="more-menu">
                  <button
                    onClick={() => { setMoreOpen(false); navigate("/login"); }}
                    className="block w-full rounded-xl px-5 py-4 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-blue-600"
                    data-testid="more-login-link"
                  >
                    Login
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            className="text-slate-900 lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-slate-200 bg-white/95 shadow-xl backdrop-blur-md lg:hidden"
          data-testid="mobile-nav"
        >
          <div className="flex flex-col px-5 py-4">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="border-b border-slate-100 py-4 text-left font-display text-lg font-medium uppercase tracking-wide text-slate-900 transition-colors hover:text-blue-600"
              >
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => setMobileSvcOpen((s) => !s)}
              className="flex items-center justify-between border-b border-slate-100 py-4 text-left font-display text-lg font-medium uppercase tracking-wide text-slate-900"
              data-testid="mobile-services-toggle"
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileSvcOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileSvcOpen && (
              <div className="grid grid-cols-2 gap-x-4" data-testid="mobile-services-list">
              {SERVICES_DATA.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-service-${s.slug}`}
                  className="border-b border-slate-100 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:text-blue-600"
                >
                  {s.nav}
                </Link>
              ))}
              </div>
            )}
            <button
              onClick={() => go("/#quote")}
              data-testid="mobile-quote-btn"
              className="mt-4 rounded-lg bg-blaze px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-blue-700"
            >
              Get Free Quote
            </button>
            <div className="mt-3 flex gap-3 pb-2">
              <button
                onClick={() => go("/login")}
                data-testid="mobile-login-link"
                className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
              >
                Login
              </button>
            </div>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
};
